import { GATrackingEvent } from '../ga-tracking-event.model';
import { GATrackingEventPayload } from '../ga-tracking-event-payload.model';
import { GaTrackingEventName, GATrackingEventType, GaTrackingStringConstantEnum } from '../../constants';
import { Product } from '~app/modules/product/models';

import get from 'lodash.get';
import { AddToCartEvent } from '~shared/modules/shopping-cart-global/models';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { Category } from '~app/modules/generic/models';
import { GetCategoryTreeNamesArray, GetLeafCategoryId } from '~app/modules/generic/helpers';
import { GaTrackingHelpersService } from '~shared/modules/ga-tracking/utils/ga-tracking-helpers.service';
import { getBlackWeekPaylodProperties } from '~shared/modules/ga-tracking/utils/ga-tracking-black-week.util';

export class GaTrackingAddToCartEvent implements GATrackingEvent {
  constructor(
    private product: Product,
    private event: AddToCartEvent,
    private priceType: PriceTypes,
    private breadcrumbs: Category[],
    private recommendation: boolean,
    private EECLabel: string,
    private isBlackDealsEnabled: boolean,
    private isVasChecked?: boolean,
    private servicePrice?: string
  ) {}

  getEventType(): GATrackingEventType {
    return GATrackingEventType.AddToCart;
  }

  getSelectedOffer() {
    const selectedOffer = this.event.offer;
    return selectedOffer
      ? get(
          this.product.offers.filter((offer) => offer.id === selectedOffer),
          '[0]',
          this.product.bestOffer
        )
      : this.product.bestOffer;
  }

  getPriceTypeAdded() {
    const offer = this.getSelectedOffer();
    const addedQuantity = this.event.quantity;
    return offer.volumePrice.filter((volumePrice) => addedQuantity >= volumePrice.quantity).length > 0
      ? GaTrackingStringConstantEnum.VolumePrice
      : GaTrackingStringConstantEnum.RegularPrice;
  }

  getPayload(): GATrackingEventPayload {
    const offer = this.getSelectedOffer();
    const isB2C = this.priceType === PriceTypes.b2c;
    const productPrice = isB2C ? get(offer, 'price.vatIncluded.amount', 0) : get(offer, 'price.vatExcluded.amount', 0);
    const dimension29 = GaTrackingHelpersService.getReferencePriceTypeFromOffer(offer);
    const dimension31 = GaTrackingHelpersService.getDimension31FromProduct(this.product);
    const dimension42 = get(
      offer,
      isB2C ? 'shippingGroup.shippingCost.unitCost.gross' : 'shippingGroup.shippingCost.unitCost.net',
      0
    );
    const leafCategoryId = GetLeafCategoryId(this.breadcrumbs);

    return {
      event: GaTrackingEventName.AddToCart,
      label: get(this.product, 'name', ''),
      gtmCustom: {
        seller: get(offer, 'organization.shopName', ''),
        priceTypeAdded: this.getPriceTypeAdded(),
        ecommerce: {
          currencyCode: GaTrackingStringConstantEnum.EUR,
          add: {
            products: [
              {
                name: get(this.product, 'name', ''),
                id: get(this.product, 'id', ''),
                price: productPrice,
                brand: get(this.product, 'brand.name', ''),
                category: GetCategoryTreeNamesArray(this.breadcrumbs).join('/'),
                category_id: leafCategoryId,
                quantity: get(this.event, 'quantity', 0),
                dimension27: get(offer, 'offerType', 'other'),
                dimension42,
                recommendation: this.recommendation
                  ? GaTrackingStringConstantEnum.Yes
                  : GaTrackingStringConstantEnum.No,
                dimension14:
                  offer && offer?.volumePrice?.length > 0
                    ? GaTrackingStringConstantEnum.Yes
                    : GaTrackingStringConstantEnum.No,
                dimension22:
                  this.product?.offers && this.product?.offers?.length > 1
                    ? GaTrackingStringConstantEnum.Yes
                    : GaTrackingStringConstantEnum.No,
                dimension12: get(this.product, 'hasPromotion', false),
                dimension15: get(offer, 'organization.shopName', ''),
                dimension32: this.EECLabel ? this.EECLabel : GaTrackingStringConstantEnum.NoEECLabel,
                dimension33: this.recommendation ? GaTrackingStringConstantEnum.Yes : GaTrackingStringConstantEnum.No,
                dimension43: GaTrackingHelpersService.getDimension43FromProduct(this.product.attributes),
                ...(!!dimension29 ? { dimension29 } : {}),
                ...(!!dimension31 ? { dimension31 } : {}),
                ...getBlackWeekPaylodProperties(this.isBlackDealsEnabled, this.product),
                dimension54: offer?.id,
                dimension58: this.isVasChecked,
                service_price: this.servicePrice,
              },
            ],
          },
        },
      },
    };
  }
}
